import { Badge } from "@/components/ui/badge";
import { CheckCircle, Clock, XCircle, AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface VerificationBadgeProps {
  userId: string;
  size?: "sm" | "md" | "lg";
}

export const VerificationBadge = ({ userId, size = "md" }: VerificationBadgeProps) => {
  const { data: verification } = useQuery({
    queryKey: ['user-verification', userId],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_verifications')
        .select('status')
        .eq('user_id', userId)
        .maybeSingle();
      
      return data?.status || 'unverified';
    }
  });

  const getVerificationConfig = (status: string) => {
    switch (status) {
      case 'verified':
        return {
          label: 'Verified Human',
          icon: CheckCircle,
          variant: 'default' as const,
          className: 'bg-green-500 hover:bg-green-600 text-white'
        };
      case 'pending':
        return {
          label: 'Verification Pending',
          icon: Clock,
          variant: 'secondary' as const,
          className: 'bg-yellow-500 hover:bg-yellow-600 text-white'
        };
      case 'rejected':
        return {
          label: 'Verification Failed',
          icon: XCircle,
          variant: 'destructive' as const,
          className: 'bg-red-500 hover:bg-red-600 text-white'
        };
      default:
        return {
          label: 'Unverified',
          icon: AlertCircle,
          variant: 'outline' as const,
          className: 'border-orange-500 text-orange-500 hover:bg-orange-50'
        };
    }
  };

  if (!verification) return null;

  const config = getVerificationConfig(verification);
  const Icon = config.icon;

  const iconSize = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  const textSize = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-xs';

  return (
    <Badge 
      variant={config.variant}
      className={`${config.className} ${textSize} flex items-center gap-1`}
    >
      <Icon className={iconSize} />
      {size !== 'sm' && config.label}
    </Badge>
  );
};